import { useMemo, useState } from "react"
import { calcularEstadoCliente } from "../../utils/estadoCliente"
import "./SeccionEstadisticas.css"

// ─────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────
const PALETTE = [
  "#3b82f6","#10b981","#f59e0b","#8b5cf6","#ef4444",
  "#06b6d4","#ec4899","#84cc16","#f97316","#6366f1",
]
const MESES_CORTOS = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"]
const TABLA_CANJE  = [{ puntos: 50, descuento: 10 }, { puntos: 100, descuento: 25 }, { puntos: 350, descuento: 100 }]

const SUBSECCIONES = [
  { id: "resumen",      label: "📊 Resumen"      },
  { id: "destinos",     label: "🌍 Destinos"     },
  { id: "clientes",     label: "👥 Clientes"     },
  { id: "operaciones",  label: "✈️ Operaciones"  },
  { id: "fidelizacion", label: "🤝 Fidelización" },
]

// ─────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────
function pts(c) {
  return (c.puntos_aporte||0)+(c.puntos_feedback||0)+(c.puntos_referidos||0)
        +(c.puntos_compra_ref||0)+(c.puntos_cumpleanos||0)-(c.puntos_usados||0)
}
function mesLabel(fechaStr) {
  if (!fechaStr) return null
  try { const d=new Date(fechaStr); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}` } catch { return null }
}
function agrupar(arr, keyFn, valFn) {
  const map = {}
  arr.forEach(item => { const k=keyFn(item); if(!k) return; map[k]=(map[k]||0)+valFn(item) })
  return Object.entries(map).sort((a,b)=>b[1]-a[1])
}
function parsearFecha(str) {
  if (!str) return null
  // Soporta DD/MM/YYYY y YYYY-MM-DD
  if (str.includes("/")) {
    const [d, m, y] = str.split("/")
    return new Date(Number(y), Number(m)-1, Number(d))
  }
  return new Date(str)
}

function diffDias(nacimiento) {
  if (!nacimiento) return null
  const hoy = new Date()
  const nac = parsearFecha(nacimiento)
  if (!nac || isNaN(nac)) return null
  let cumple = new Date(hoy.getFullYear(), nac.getMonth(), nac.getDate())
  if (cumple < hoy) cumple.setFullYear(hoy.getFullYear() + 1)
  return Math.round((cumple - hoy) / (1000*60*60*24))
}
function colorVariacion(pct) {
  if (pct === null) return "#4b5563"
  if (pct >= 10)  return "#10b981"
  if (pct >= 0)   return "#f59e0b"
  return "#ef4444"
}
function flechaVariacion(pct) {
  if (pct === null) return "—"
  if (pct >= 10)  return "▲"
  if (pct >= 0)   return "►"
  return "▼"
}
function formatPesos(n) { return `$${(n||0).toLocaleString("es-AR")}` }
function truncar(str, max=18) { return str && str.length>max ? str.slice(0,max)+"…" : str }

// ─────────────────────────────────────────────────
// Componentes reutilizables
// ─────────────────────────────────────────────────
function KpiCard({ label, valor, sub, color, icono, varPct }) {
  return (
    <div className="est-kpi" style={{ borderTop:`3px solid ${color||"#3b82f6"}` }}>
      <div className="est-kpi__icono">{icono}</div>
      <div className="est-kpi__valor">{valor}</div>
      <div className="est-kpi__label">{label}</div>
      {varPct !== undefined && (
        <div style={{ fontSize:"0.75rem", fontWeight:700, color:colorVariacion(varPct), marginTop:"4px" }}>
          {flechaVariacion(varPct)} {varPct!==null ? `${varPct>=0?"+":""}${varPct}% vs mes ant.` : "Sin datos previos"}
        </div>
      )}
      {sub && !varPct && <div className="est-kpi__sub">{sub}</div>}
    </div>
  )
}

function BarraH({ label, valor, maximo, color, prefix="", suffix="" }) {
  const pct = maximo>0 ? Math.round((valor/maximo)*100) : 0
  return (
    <div className="est-barra">
      <div className="est-barra__label" title={label}>{truncar(label)}</div>
      <div className="est-barra__track"><div className="est-barra__fill" style={{ width:`${pct}%`, background:color||"#3b82f6" }}/></div>
      <div className="est-barra__valor">{prefix}{typeof valor==="number"?valor.toLocaleString("es-AR"):valor}{suffix}</div>
    </div>
  )
}

function GraficoBarrasV({ datos, color, altura=140, prefix="$" }) {
  const max = Math.max(...datos.map(d=>d.valor), 1)
  return (
    <div className="est-grafico-barra" style={{ height:altura }}>
      {datos.map((d,i) => (
        <div key={i} className="est-grafico-barra__col">
          <div className="est-grafico-barra__barra-wrap">
            <div
              className="est-grafico-barra__barra"
              style={{ height:`${Math.round((d.valor/max)*100)}%`, background:color||"#3b82f6" }}
              title={`${d.label}: ${prefix}${d.valor.toLocaleString("es-AR")}`}
            />
          </div>
          <div className="est-grafico-barra__mes">{d.label}</div>
        </div>
      ))}
    </div>
  )
}

function Donut({ segmentos, total, label="total" }) {
  let acum = 0
  const parts = segmentos.map(s => {
    const pct = total>0 ? (s.valor/total)*100 : 0
    const p = `${s.color} ${acum.toFixed(1)}% ${(acum+pct).toFixed(1)}%`
    acum += pct
    return p
  })
  return (
    <div className="est-donut-wrap">
      <div className="est-donut" style={{ background:`conic-gradient(${parts.join(", ")})` }}>
        <div className="est-donut__centro">
          <span className="est-donut__total">{total}</span>
          <span className="est-donut__sub">{label}</span>
        </div>
      </div>
      <div className="est-donut__leyenda">
        {segmentos.map((s,i) => (
          <div key={i} className="est-donut__item">
            <span className="est-donut__punto" style={{ background:s.color }}/>
            <span className="est-donut__nombre">{s.label}</span>
            <span className="est-donut__pct">{total>0?Math.round((s.valor/total)*100):0}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Funnel({ pasos }) {
  const max = pasos[0]?.valor || 1
  return (
    <div className="est-funnel">
      {pasos.map((p, i) => {
        const pct = Math.round((p.valor/max)*100)
        const conv = i>0 && pasos[i-1].valor>0 ? Math.round((p.valor/pasos[i-1].valor)*100) : null
        return (
          <div key={p.label} className="est-funnel__paso">
            <div className="est-funnel__barra-wrap">
              <div className="est-funnel__barra" style={{ width:`${pct}%`, background:p.color }}>
                <span className="est-funnel__barra-valor">{p.valor}</span>
              </div>
            </div>
            <div className="est-funnel__info">
              <span className="est-funnel__label">{p.label}</span>
              {conv !== null && (
                <span className="est-funnel__conv" style={{ color: conv>=50?"#10b981":conv>=25?"#f59e0b":"#ef4444" }}>
                  {conv}% del paso anterior
                </span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─────────────────────────────────────────────────
// Heatmap de estacionalidad
// ─────────────────────────────────────────────────
function HeatmapEstacionalidad({ viajes }) {
  const topDestinos = useMemo(() => {
    const cnt = {}
    viajes.forEach(v => { if (v.destino) cnt[v.destino]=(cnt[v.destino]||0)+1 })
    return Object.entries(cnt).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([d])=>d)
  }, [viajes])

  const matriz = useMemo(() => {
    const m = {}
    topDestinos.forEach(d => { m[d] = new Array(12).fill(0) })
    viajes.forEach(v => {
      if (!v.fecha_salida || !topDestinos.includes(v.destino)) return
      const mes = new Date(v.fecha_salida).getMonth()
      m[v.destino][mes]++
    })
    return m
  }, [viajes, topDestinos])

  const maxVal = Math.max(...topDestinos.flatMap(d => matriz[d] || []), 1)

  if (topDestinos.length === 0) return <div className="est-empty">Sin datos suficientes</div>

  return (
    <div className="est-heatmap">
      <div className="est-heatmap__grid" style={{ gridTemplateColumns:`140px repeat(12, 1fr)` }}>
        <div className="est-heatmap__corner"/>
        {MESES_CORTOS.map(m => <div key={m} className="est-heatmap__mes-header">{m}</div>)}
        {topDestinos.map(dest => (
          <>
            <div key={dest+"label"} className="est-heatmap__dest-label" title={dest}>{truncar(dest, 16)}</div>
            {(matriz[dest]||[]).map((val, mi) => {
              const intensity = maxVal>0 ? val/maxVal : 0
              const bg = val===0 ? "#1a1a1a" : `rgba(59,130,246,${0.15+intensity*0.85})`
              return (
                <div key={mi} className="est-heatmap__celda" style={{ background:bg }} title={`${dest} — ${MESES_CORTOS[mi]}: ${val} viajes`}>
                  {val>0 && <span>{val}</span>}
                </div>
              )
            })}
          </>
        ))}
      </div>
      <div style={{ fontSize:"0.75rem", color:"#4b5563", marginTop:"10px", textAlign:"right" }}>
        Intensidad = cantidad de viajes por mes de salida
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────
// Clientes en riesgo
// ─────────────────────────────────────────────────
function ClientesEnRiesgo({ clientes, viajesPorCliente }) {
  const [descartados, setDescartados] = useState([])
  const hoy = new Date()

  const clasificados = useMemo(() => {
    return clientes.map(c => {
      const viajes = viajesPorCliente[c.id] || []
      const viajesFuturos  = viajes.filter(v => v.estado !== "cancelado" && new Date(v.fecha_salida) > hoy)
      const viajesPasados  = viajes.filter(v => new Date(v.fecha_salida) <= hoy).sort((a,b) => new Date(b.fecha_salida)-new Date(a.fecha_salida))
      const ultimoViaje    = viajesPasados[0]
      const tieneRegistro  = true
      const diasDesdeUltimo = ultimoViaje ? Math.round((hoy - new Date(ultimoViaje.fecha_salida))/(1000*60*60*24)) : null
      const nunca = viajes.length === 0

      if (viajesFuturos.length > 0) return null // tiene viaje próximo, no es riesgo

      if (nunca) {
        const diasRegistrado = c.creado_en?.seconds
          ? Math.round((hoy - new Date(c.creado_en.seconds*1000))/(1000*60*60*24))
          : null
        if (diasRegistrado && diasRegistrado >= 90) return { ...c, riesgo: "perdido", dias: diasRegistrado, label: "Registrado sin viajar", ultimoDestino: null }
        return null
      }

      if (diasDesdeUltimo >= 365) return { ...c, riesgo: "frio", dias: diasDesdeUltimo, label: `${Math.round(diasDesdeUltimo/30)} meses sin viajar`, ultimoDestino: ultimoViaje.destino }
      if (diasDesdeUltimo >= 180) return { ...c, riesgo: "tibio", dias: diasDesdeUltimo, label: `${Math.round(diasDesdeUltimo/30)} meses sin viajar`, ultimoDestino: ultimoViaje.destino }

      return null
    }).filter(Boolean).filter(c => !descartados.includes(c.id))
      .sort((a,b) => {
        const orden = { frio:0, perdido:1, tibio:2 }
        return (orden[a.riesgo]??9) - (orden[b.riesgo]??9)
      })
  }, [clientes, viajesPorCliente, descartados])

  const colores = { frio:"#ef4444", tibio:"#f59e0b", perdido:"#4b5563" }
  const iconos  = { frio:"🔴", tibio:"🟡", perdido:"⚫" }
  const frios   = clasificados.filter(c=>c.riesgo==="frio")
  const tibios  = clasificados.filter(c=>c.riesgo==="tibio")
  const perdidos= clasificados.filter(c=>c.riesgo==="perdido")

  return (
    <div>
      <div className="est-riesgo-resumen">
        {[
          { tipo:"frio",    lista:frios,    label:"Fríos",    sub:"+12 meses sin viajar",           icono:"🔴" },
          { tipo:"tibio",   lista:tibios,   label:"Tibios",   sub:"6-12 meses sin viajar",          icono:"🟡" },
          { tipo:"perdido", lista:perdidos, label:"Sin viajar",sub:"Registrados sin viaje >90 días",icono:"⚫" },
        ].map(g => (
          <div key={g.tipo} className="est-riesgo-badge" style={{ borderColor:colores[g.tipo]+"60", background:colores[g.tipo]+"10" }}>
            <span style={{ fontSize:"1.5rem" }}>{g.icono}</span>
            <div>
              <div style={{ fontSize:"1.4rem", fontWeight:800, color:colores[g.tipo] }}>{g.lista.length}</div>
              <div style={{ fontSize:"0.75rem", fontWeight:700, color:"#e5e7eb" }}>{g.label}</div>
              <div style={{ fontSize:"0.7rem", color:"#6b7280" }}>{g.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {clasificados.length === 0 ? (
        <div className="est-empty">✅ No hay clientes en riesgo actualmente</div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:"8px", marginTop:"16px" }}>
          {clasificados.map(c => (
            <div key={c.id} className="est-riesgo-card" style={{ borderLeft:`3px solid ${colores[c.riesgo]}` }}>
              <div className="est-riesgo-card__info">
                <div style={{ fontWeight:700, color:"#e5e7eb", fontSize:"0.9rem" }}>{c.nombre} {c.apellido}</div>
                <div style={{ fontSize:"0.78rem", color:"#6b7280", marginTop:"2px" }}>
                  {iconos[c.riesgo]} {c.label}
                  {c.ultimoDestino && <span style={{ color:"#4b5563" }}> · Último: {c.ultimoDestino}</span>}
                  {c.ciudad && <span style={{ color:"#4b5563" }}> · {c.ciudad}</span>}
                </div>
              </div>
              <div style={{ display:"flex", gap:"6px", flexShrink:0 }}>
                {c.telefono && (
                  <a
                    href={`https://wa.me/${c.telefono.replace(/\D/g,"")}?text=Hola ${c.nombre}! Soy Sergio de Low Travel. Tengo una propuesta de viaje que te puede interesar`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ padding:"6px 12px", background:"#25d36620", border:"1px solid #25d36640", borderRadius:"6px", color:"#25d366", fontSize:"0.75rem", fontWeight:700, textDecoration:"none", whiteSpace:"nowrap" }}
                  >
                    💬 WhatsApp
                  </a>
                )}
                <button
                  onClick={() => setDescartados(p=>[...p, c.id])}
                  style={{ padding:"6px 10px", background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:"6px", color:"#4b5563", fontSize:"0.75rem", cursor:"pointer", whiteSpace:"nowrap" }}
                  title="Marcar como gestionado"
                >
                  ✓ Gestionar
                </button>
              </div>
            </div>
          ))}
          {descartados.length > 0 && (
            <button onClick={()=>setDescartados([])} style={{ background:"none", border:"none", color:"#4b5563", fontSize:"0.75rem", cursor:"pointer", textAlign:"left", marginTop:"4px" }}>
              Restablecer {descartados.length} oculto{descartados.length!==1?"s":""}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────
export default function SeccionEstadisticas({ viajes, clientes, viajesPorCliente }) {
  const [subseccion, setSubseccion] = useState("resumen")
  const [periodo, setPeriodo]       = useState("todo")
  const [vistaDestino, setVistaDestino] = useState("revenue") // revenue | pax | cantidad

  const ahora = new Date()

  const viajesFiltrados = useMemo(() => {
    return viajes.filter(v => {
      if (!v.fecha_salida) return periodo === "todo"
      const d = new Date(v.fecha_salida)
      if (periodo === "anio") return d.getFullYear() === ahora.getFullYear()
      if (periodo === "mes")  return d.getFullYear() === ahora.getFullYear() && d.getMonth() === ahora.getMonth()
      return true
    })
  }, [viajes, periodo])

  // ── KPIs ──────────────────────────────────────
  const totalRevenue   = viajesFiltrados.reduce((a,v)=>a+(v.monto_venta||0),0)
  const totalPax       = viajesFiltrados.reduce((a,v)=>a+(v.pax||0),0)
  const totalNoches    = viajesFiltrados.reduce((a,v)=>a+(v.noches||0),0)
  const ticketPromedio = viajesFiltrados.length>0 ? Math.round(totalRevenue/viajesFiltrados.length) : 0
  const paxPromedio    = viajesFiltrados.length>0 ? (totalPax/viajesFiltrados.length).toFixed(1) : 0
  const nochesPromedio = viajesFiltrados.length>0 ? (totalNoches/viajesFiltrados.length).toFixed(1) : 0

  const viajesEsteMes  = viajes.filter(v=>{ if(!v.fecha_salida)return false; const d=new Date(v.fecha_salida); return d.getFullYear()===ahora.getFullYear()&&d.getMonth()===ahora.getMonth() })
  const viajesMesAnt   = viajes.filter(v=>{ if(!v.fecha_salida)return false; const d=new Date(v.fecha_salida); const ma=new Date(ahora.getFullYear(),ahora.getMonth()-1,1); return d.getFullYear()===ma.getFullYear()&&d.getMonth()===ma.getMonth() })
  const revenueEsteMes = viajesEsteMes.reduce((a,v)=>a+(v.monto_venta||0),0)
  const revenueMesAnt  = viajesMesAnt.reduce((a,v)=>a+(v.monto_venta||0),0)
  const varMes         = revenueMesAnt>0 ? Math.round(((revenueEsteMes-revenueMesAnt)/revenueMesAnt)*100) : null
  const conAsistencia  = viajesFiltrados.filter(v=>v.asistencia==="Si").length
  const pctAsistencia  = viajesFiltrados.length>0 ? Math.round((conAsistencia/viajesFiltrados.length)*100) : 0

  // ── Tendencia 12 meses ────────────────────────
  const tendenciaMeses = useMemo(() => {
    const meses = {}
    for (let i=11; i>=0; i--) {
      const d = new Date(ahora.getFullYear(), ahora.getMonth()-i, 1)
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`
      const label = d.toLocaleDateString("es-AR",{month:"short"}).replace(".","")
      meses[key] = { label, valor:0, viajes:0 }
    }
    viajes.forEach(v => { const k=mesLabel(v.fecha_salida); if(k&&meses[k]){ meses[k].valor+=(v.monto_venta||0); meses[k].viajes++ } })
    return Object.values(meses)
  }, [viajes])

  // ── Destinos ──────────────────────────────────
  const topDestinosRevenue  = agrupar(viajesFiltrados, v=>v.destino, v=>v.monto_venta||0).filter(([k])=>k).slice(0,10)
  const topDestinosPax      = agrupar(viajesFiltrados, v=>v.destino, v=>v.pax||0).filter(([k])=>k).slice(0,10)
  const topDestinosCantidad = agrupar(viajesFiltrados, v=>v.destino, ()=>1).filter(([k])=>k).slice(0,10)
  const destinosActivos = vistaDestino==="revenue" ? topDestinosRevenue : vistaDestino==="pax" ? topDestinosPax : topDestinosCantidad
  const maxDestino = destinosActivos[0]?.[1]||1
  const prefixDestino = vistaDestino==="revenue" ? "$" : ""
  const suffixDestino = vistaDestino==="pax" ? " pax" : vistaDestino==="cantidad" ? " viajes" : ""

  // ── Clientes ──────────────────────────────────
  const clientesConViaje = clientes.filter(c=>(viajesPorCliente[c.id]||[]).length>0).length
  const clientesRepeat   = clientes.filter(c=>(viajesPorCliente[c.id]||[]).length>1).length
  const pctRepeat        = clientesConViaje>0 ? Math.round((clientesRepeat/clientesConViaje)*100) : 0

  const topLTV = useMemo(() => {
    return clientes.map(c => {
      const vs = viajesPorCliente[c.id]||[]
      const ltv = vs.reduce((a,v)=>a+(v.monto_venta||0),0)
      const nViajes = vs.length
      return { ...c, ltv, nViajes }
    }).filter(c=>c.ltv>0).sort((a,b)=>b.ltv-a.ltv).slice(0,10)
  }, [clientes, viajesPorCliente])
  const maxLTV = topLTV[0]?.ltv||1

  const porCiudad = agrupar(clientes, c=>c.ciudad||c.origen, ()=>1).filter(([k])=>k).slice(0,8)
  const maxCiudad = porCiudad[0]?.[1]||1

  const proximosCumples = clientes
    .map(c=>({ ...c, dias:diffDias(c.nacimiento) }))
    .filter(c=>c.dias!==null&&c.dias<=30)
    .sort((a,b)=>a.dias-b.dias).slice(0,12)

  // ── Operaciones ───────────────────────────────
  const topAerolineas  = agrupar(viajesFiltrados, v=>v.aerolinea, v=>v.monto_venta||0).filter(([k])=>k).slice(0,8)
  const maxAero        = topAerolineas[0]?.[1]||1
  const porCanal       = agrupar(viajesFiltrados, v=>v.canal_venta, v=>v.monto_venta||0).filter(([k])=>k)
  const maxCanal       = porCanal[0]?.[1]||1
  const porEstado      = agrupar(viajesFiltrados, v=>v.estado, ()=>1)
  const maxEstado      = porEstado[0]?.[1]||1

  const donutCanales = porCanal.slice(0,5).map(([label,valor],i)=>({ label, valor, color:PALETTE[i%PALETTE.length] }))
  const totalCanal   = porCanal.reduce((a,[,v])=>a+v,0)

  // ── Fidelización ──────────────────────────────
  const referidos    = viajesFiltrados.filter(v=>v.referido_por&&v.referido_por.trim()!=="")
  const porReferido  = agrupar(referidos, v=>v.referido_por, ()=>1).slice(0,8)
  const maxRef       = porReferido[0]?.[1]||1
  const pctReferidos = viajesFiltrados.length>0 ? Math.round((referidos.length/viajesFiltrados.length)*100) : 0

  const topPuntos = [...clientes].map(c=>({...c,total_pts:pts(c)})).filter(c=>c.total_pts>0).sort((a,b)=>b.total_pts-a.total_pts).slice(0,10)
  const totalPuntosActivos = clientes.reduce((a,c)=>a+pts(c),0)
  const clientesConPuntosCanje = clientes.filter(c=>pts(c)>=50).length

  // ─────────────────────────────────────────────
  return (
    <div className="est">

      {/* ── Header ── */}
      <div className="est__header">
        <div>
          <div className="admin__seccion-titulo">Estadísticas</div>
          <div className="admin__seccion-sub">Panel de análisis del negocio</div>
        </div>
        <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", alignItems:"center" }}>
          <div className="est__periodo-selector">
            {[{id:"todo",label:"Todo"},{id:"anio",label:"Este año"},{id:"mes",label:"Este mes"}].map(p=>(
              <button key={p.id} onClick={()=>setPeriodo(p.id)} className={"est__periodo-btn"+(periodo===p.id?" active":"")}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Sub-navegación temática ── */}
      <div className="est__subnav">
        {SUBSECCIONES.map(s=>(
          <button key={s.id} onClick={()=>setSubseccion(s.id)} className={"est__subnav-btn"+(subseccion===s.id?" active":"")}>
            {s.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════
          SUBSECCIÓN 1 — RESUMEN
      ══════════════════════════════════════════ */}
      {subseccion==="resumen" && (
        <div>
          {/* KPIs */}
          <div className="est-kpi-grid" style={{ marginBottom:"20px" }}>
            <KpiCard label="Ingresos totales"   valor={formatPesos(totalRevenue)}      icono="💰" color="#10b981" varPct={varMes} />
            <KpiCard label="Viajes"             valor={viajesFiltrados.length}         icono="✈️" color="#3b82f6" />
            <KpiCard label="Ticket promedio"    valor={formatPesos(ticketPromedio)}    icono="🎫" color="#f59e0b" />
            <KpiCard label="Clientes"           valor={clientes.length}               icono="👥" color="#8b5cf6" />
            <KpiCard label="Este mes"           valor={formatPesos(revenueEsteMes)}   icono="📅" color="#06b6d4" sub={`${viajesEsteMes.length} viajes`} />
            <KpiCard label="Pax promedio"       valor={paxPromedio}                   icono="🧳" color="#ec4899" />
            <KpiCard label="Noches promedio"    valor={nochesPromedio}                icono="🌙" color="#f97316" />
            <KpiCard label="Con asistencia"     valor={`${pctAsistencia}%`}           icono="🛡️" color="#84cc16" sub={`${conAsistencia} viajes`} />
          </div>

          {/* Tendencia 12 meses */}
          <div className="est-card" style={{ marginBottom:"20px" }}>
            <div className="est-card__titulo">📈 Tendencia de ingresos — últimos 12 meses</div>
            <GraficoBarrasV datos={tendenciaMeses} color="#3b82f6" altura={160} />
            <div style={{ display:"flex", justifyContent:"space-between", marginTop:"12px", paddingTop:"12px", borderTop:"1px solid #1a1a1a" }}>
              <div style={{ fontSize:"0.75rem", color:"#4b5563" }}>Ingresos por mes de salida del viaje</div>
              <div style={{ fontSize:"0.875rem", fontWeight:700, color:colorVariacion(varMes) }}>
                {flechaVariacion(varMes)} {varMes!==null ? `${varMes>=0?"+":""}${varMes}% vs mes anterior` : "Sin datos previos"}
              </div>
            </div>
          </div>

          {/* Resumen rápido */}
          <div className="est-grid-2">
            <div className="est-card">
              <div className="est-card__titulo">📊 Viajes por estado</div>
              <div className="est-barras-lista">
                {porEstado.length===0 ? <div className="est-empty">Sin datos</div> :
                  porEstado.map(([label,valor],i)=>(
                    <BarraH key={label} label={label.charAt(0).toUpperCase()+label.slice(1)} valor={valor} maximo={maxEstado} color={PALETTE[i%PALETTE.length]} suffix=" viajes"/>
                  ))
                }
              </div>
            </div>
            <div className="est-card">
              <div className="est-card__titulo">🏆 Resumen de fidelización</div>
              <div className="est-grid-3" style={{ marginBottom:"12px" }}>
                <div className="est-stat-box"><div className="est-stat-box__valor" style={{ color:"#3b82f6" }}>{clientes.length}</div><div className="est-stat-box__label">Clientes</div></div>
                <div className="est-stat-box"><div className="est-stat-box__valor" style={{ color:"#10b981" }}>{clientesConViaje}</div><div className="est-stat-box__label">Viajaron</div></div>
                <div className="est-stat-box"><div className="est-stat-box__valor" style={{ color:"#f59e0b" }}>{clientesRepeat}</div><div className="est-stat-box__label">Repitieron</div></div>
              </div>
              <div style={{ fontSize:"0.875rem", color:"#9ca3af", textAlign:"center" }}>
                Tasa de repetición: <strong style={{ color:pctRepeat>=30?"#10b981":pctRepeat>=15?"#f59e0b":"#ef4444" }}>{pctRepeat}%</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          SUBSECCIÓN 2 — DESTINOS
      ══════════════════════════════════════════ */}
      {subseccion==="destinos" && (
        <div>
          <div className="est-card" style={{ marginBottom:"20px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px", paddingBottom:"12px", borderBottom:"1px solid #1a1a1a" }}>
              <div className="est-card__titulo" style={{ margin:0, border:0, padding:0 }}>🌍 Destinos</div>
              <div className="est__periodo-selector">
                {[{id:"revenue",label:"Ingresos"},{id:"pax",label:"Pasajeros"},{id:"cantidad",label:"Viajes"}].map(v=>(
                  <button key={v.id} onClick={()=>setVistaDestino(v.id)} className={"est__periodo-btn"+(vistaDestino===v.id?" active":"")}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            {destinosActivos.length===0 ? <div className="est-empty">Sin datos</div> :
              destinosActivos.map(([label,valor],i)=>(
                <BarraH key={label} label={label} valor={valor} maximo={maxDestino} color={PALETTE[i%PALETTE.length]} prefix={prefixDestino} suffix={suffixDestino}/>
              ))
            }
          </div>

          <div className="est-card">
            <div className="est-card__titulo">🗓️ Estacionalidad — ¿cuándo viaja cada destino?</div>
            <div style={{ fontSize:"0.8rem", color:"#6b7280", marginBottom:"16px" }}>
              Top 6 destinos por mes de salida. Útil para planificar campañas de Meta Ads con anticipación.
            </div>
            <HeatmapEstacionalidad viajes={viajes} />
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          SUBSECCIÓN 3 — CLIENTES
      ══════════════════════════════════════════ */}
      {subseccion==="clientes" && (
        <div>
          {/* Funnel */}
          <div className="est-card" style={{ marginBottom:"20px" }}>
            <div className="est-card__titulo">🔻 Embudo de clientes</div>
            <Funnel pasos={[
              { label:"Clientes registrados",      valor:clientes.length,     color:"#3b82f6" },
              { label:"Con al menos 1 viaje",       valor:clientesConViaje,    color:"#8b5cf6" },
              { label:"Repitieron (2+ viajes)",     valor:clientesRepeat,      color:"#10b981" },
            ]}/>
          </div>

          {/* Clientes en riesgo */}
          <div className="est-card" style={{ marginBottom:"20px" }}>
            <div className="est-card__titulo">⚠️ Clientes en riesgo de abandono</div>
            <div style={{ fontSize:"0.8rem", color:"#6b7280", marginBottom:"16px" }}>
              Clientes sin viaje futuro, ordenados por urgencia. Hacé click en WhatsApp para contactarlos directo.
            </div>
            <ClientesEnRiesgo clientes={clientes} viajesPorCliente={viajesPorCliente} />
          </div>

          {/* LTV */}
          <div className="est-card" style={{ marginBottom:"20px" }}>
            <div className="est-card__titulo">💎 Top clientes por valor total (LTV)</div>
            <div style={{ fontSize:"0.8rem", color:"#6b7280", marginBottom:"16px" }}>Ingresos acumulados por cliente en toda la historia.</div>
            <div className="est-barras-lista">
              {topLTV.map((c,i)=>(
                <div key={c.id} style={{ display:"grid", gridTemplateColumns:"24px 1fr auto", alignItems:"center", gap:"10px" }}>
                  <span style={{ fontSize:"0.75rem", color:"#4b5563", fontWeight:700 }}>#{i+1}</span>
                  <BarraH label={`${c.nombre} ${c.apellido}`} valor={c.ltv} maximo={maxLTV} color={PALETTE[i%PALETTE.length]} prefix="$"/>
                  <span style={{ fontSize:"0.75rem", color:"#6b7280", whiteSpace:"nowrap" }}>{c.nViajes} viaje{c.nViajes!==1?"s":""}</span>
                </div>
              ))}
              {topLTV.length===0 && <div className="est-empty">Sin datos de ventas</div>}
            </div>
          </div>

          {/* Ciudad + Cumpleaños */}
          <div className="est-grid-2" style={{ marginBottom:"20px" }}>
            <div className="est-card">
              <div className="est-card__titulo">📍 Clientes por ciudad</div>
              <div className="est-barras-lista">
                {porCiudad.length===0 ? <div className="est-empty">Sin datos</div> :
                  porCiudad.map(([label,valor],i)=>(
                    <BarraH key={label} label={label} valor={valor} maximo={maxCiudad} color={PALETTE[(i+2)%PALETTE.length]} suffix=" clientes"/>
                  ))
                }
              </div>
            </div>
            <div className="est-card">
              <div className="est-card__titulo">🎂 Próximos cumpleaños — 30 días</div>
              {proximosCumples.length===0 ? (
                <div className="est-empty">No hay cumpleaños en los próximos 30 días</div>
              ) : (
                <div className="est-cumples-grid">
                  {proximosCumples.map(c => {
                    const esHoy = c.dias===0, urgente = c.dias<=3
                    return (
                      <div key={c.id} className={"est-cumple-card"+(esHoy?" est-cumple-card--hoy":urgente?" est-cumple-card--urgente":"")}>
                        <div className="est-cumple-card__icono">{esHoy?"🎉":urgente?"🎂":"📅"}</div>
                        <div className="est-cumple-card__info">
                          <div className="est-cumple-card__nombre">{c.nombre} {c.apellido}</div>
                          <div className="est-cumple-card__dias">{esHoy?"¡Hoy!":`En ${c.dias} día${c.dias!==1?"s":""}`}</div>
                        </div>
                        <div className="est-cumple-card__ciudad">{c.ciudad||""}</div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          SUBSECCIÓN 4 — OPERACIONES
      ══════════════════════════════════════════ */}
      {subseccion==="operaciones" && (
        <div>
          <div className="est-grid-2" style={{ marginBottom:"20px" }}>
            <div className="est-card">
              <div className="est-card__titulo">✈️ Aerolíneas por ingresos</div>
              <div className="est-barras-lista">
                {topAerolineas.length===0 ? <div className="est-empty">Sin datos</div> :
                  topAerolineas.map(([label,valor],i)=>(
                    <BarraH key={label} label={label} valor={valor} maximo={maxAero} color={PALETTE[(i+3)%PALETTE.length]} prefix="$"/>
                  ))
                }
              </div>
            </div>
            <div className="est-card">
              <div className="est-card__titulo">📣 Canales de venta</div>
              {donutCanales.length===0 ? <div className="est-empty">Sin datos</div> : (
                <>
                  <Donut segmentos={donutCanales} total={viajesFiltrados.filter(v=>v.canal_venta).length} label="viajes" />
                  <div className="est-card__footer">Ingresos por canal más usado: <strong style={{ color:"#10b981" }}>{formatPesos(porCanal[0]?.[1]||0)}</strong></div>
                </>
              )}
            </div>
          </div>

          <div className="est-grid-2" style={{ marginBottom:"20px" }}>
            <div className="est-card">
              <div className="est-card__titulo">📊 Estado de viajes</div>
              <div className="est-barras-lista">
                {porEstado.length===0 ? <div className="est-empty">Sin datos</div> :
                  porEstado.map(([label,valor],i)=>(
                    <BarraH key={label} label={label.charAt(0).toUpperCase()+label.slice(1)} valor={valor} maximo={maxEstado} color={PALETTE[i%PALETTE.length]} suffix=" viajes"/>
                  ))
                }
              </div>
            </div>
            <div className="est-card">
              <div className="est-card__titulo">📐 Promedios operativos</div>
              <div className="est-grid-3">
                <div className="est-stat-box">
                  <div className="est-stat-box__valor" style={{ color:"#ec4899" }}>{paxPromedio}</div>
                  <div className="est-stat-box__label">Pax promedio</div>
                </div>
                <div className="est-stat-box">
                  <div className="est-stat-box__valor" style={{ color:"#f97316" }}>{nochesPromedio}</div>
                  <div className="est-stat-box__label">Noches promedio</div>
                </div>
                <div className="est-stat-box">
                  <div className="est-stat-box__valor" style={{ color:"#84cc16" }}>{pctAsistencia}%</div>
                  <div className="est-stat-box__label">Con asistencia</div>
                </div>
              </div>
              <div style={{ marginTop:"16px", borderTop:"1px solid #1a1a1a", paddingTop:"14px" }}>
                <div style={{ fontSize:"0.75rem", color:"#4b5563", marginBottom:"8px", fontWeight:700, textTransform:"uppercase" }}>Desglose canales</div>
                {porCanal.map(([label,valor],i)=>(
                  <BarraH key={label} label={label} valor={valor} maximo={maxCanal} color={PALETTE[(i+5)%PALETTE.length]} prefix="$"/>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          SUBSECCIÓN 5 — FIDELIZACIÓN
      ══════════════════════════════════════════ */}
      {subseccion==="fidelizacion" && (
        <div>
          {/* Stats de puntos */}
          <div className="est-card" style={{ marginBottom:"20px" }}>
            <div className="est-card__titulo">⭐ Sistema de puntos</div>
            <div className="est-grid-3" style={{ marginBottom:"16px" }}>
              <div className="est-stat-box">
                <div className="est-stat-box__valor" style={{ color:"#f59e0b" }}>{totalPuntosActivos.toLocaleString("es-AR")}</div>
                <div className="est-stat-box__label">Puntos activos en el sistema</div>
              </div>
              <div className="est-stat-box">
                <div className="est-stat-box__valor" style={{ color:"#10b981" }}>{clientesConPuntosCanje}</div>
                <div className="est-stat-box__label">Pueden canjear ahora</div>
              </div>
              <div className="est-stat-box">
                <div className="est-stat-box__valor" style={{ color:"#3b82f6" }}>{pctRepeat}%</div>
                <div className="est-stat-box__label">Tasa de repetición</div>
              </div>
            </div>
            {clientesConPuntosCanje > 0 && (
              <div style={{ padding:"12px 16px", background:"#1a2a1a", border:"1px solid #10b98130", borderRadius:"10px", fontSize:"0.85rem", color:"#10b981" }}>
                💡 <strong>{clientesConPuntosCanje} clientes</strong> tienen suficientes puntos para canjear descuentos. Es una buena oportunidad para contactarlos y cerrar un viaje.
              </div>
            )}
          </div>

          {/* Referidos */}
          <div className="est-grid-2" style={{ marginBottom:"20px" }}>
            <div className="est-card">
              <div className="est-card__titulo">🤝 Referidos</div>
              <div style={{ display:"flex", alignItems:"center", gap:"16px", marginBottom:"16px", padding:"14px", background:"#1a1a1a", borderRadius:"10px" }}>
                <div style={{ fontSize:"2.5rem", fontWeight:800, color:pctReferidos>=20?"#10b981":pctReferidos>=10?"#f59e0b":"#6b7280" }}>{pctReferidos}%</div>
                <div>
                  <div style={{ fontSize:"0.875rem", color:"#e5e7eb", fontWeight:600 }}>de los viajes vienen por referidos</div>
                  <div style={{ fontSize:"0.8rem", color:"#6b7280", marginTop:"2px" }}>{referidos.length} de {viajesFiltrados.length} viajes</div>
                </div>
              </div>
              <div className="est-card__subtitulo" style={{ marginBottom:"10px", fontWeight:700, color:"#e5e7eb" }}>Top referidores</div>
              <div className="est-barras-lista">
                {porReferido.length===0 ? <div className="est-empty">Sin referidos registrados</div> :
                  porReferido.map(([label,valor],i)=>(
                    <BarraH key={label} label={label} valor={valor} maximo={maxRef} color={PALETTE[(i+4)%PALETTE.length]} suffix=" viajes"/>
                  ))
                }
              </div>
            </div>
            <div className="est-card">
              <div className="est-card__titulo">🏅 Top clientes por puntos</div>
              <div style={{ overflowX:"auto" }}>
                <table className="est-tabla">
                  <thead>
                    <tr><th>#</th><th>Cliente</th><th style={{ textAlign:"right" }}>Puntos</th><th style={{ textAlign:"right" }}>Viajes</th></tr>
                  </thead>
                  <tbody>
                    {topPuntos.length===0 ? (
                      <tr><td colSpan={4} style={{ textAlign:"center", color:"#4b5563", padding:"20px" }}>Sin datos</td></tr>
                    ) : topPuntos.map((c,i)=>(
                      <tr key={c.id}>
                        <td className="est-tabla__rank">#{i+1}</td>
                        <td style={{ color:"#e5e7eb", fontWeight:600 }}>{c.nombre} {c.apellido}</td>
                        <td style={{ textAlign:"right", color:"#f59e0b", fontWeight:700 }}>{c.total_pts}</td>
                        <td style={{ textAlign:"right", color:"#9ca3af" }}>{(viajesPorCliente[c.id]||[]).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}